/**
 * Singleton fetcher for `GET /building/:id/chat`.
 *
 * `/building/:id/chat` is the heavy "list every chat group of a building"
 * endpoint (~1-3 s on cold caches; backed by a stored procedure plus the
 * BuildingChatCache Redis layer). It is called from:
 *
 *   - the `ProjectDetail` mount-effect,
 *   - the silent-refresh setTimeout that runs after the preloaded-from-
 *     LatestMessages seed has been painted,
 *   - several mutation-completion callbacks that re-fetch to reflect a
 *     server-side change.
 *
 * In dev React.StrictMode double-runs every effect, and the
 * `chatsFetchedForProjectRef` guard inside `ProjectDetail` is set only
 * AFTER the async `getChats()` resolves. The window between the two
 * StrictMode runs is therefore enough time for a second identical
 * `/building/:id/chat` request to be issued before the first one returns.
 * The browser then has two near-identical pending requests (and only six
 * connection slots to `localhost` on HTTP/1.1) — the second one sits in
 * "Pending" / "Stalled" state for the duration of the first.
 *
 * This helper does the same thing the badgeCount wrapper does:
 *
 *   1. In-flight dedupe — concurrent callers for the same building share a
 *      single Promise. The BE only sees one hit; every caller resolves
 *      with the same response.
 *   2. Short post-flight reuse — for `BUILDING_CHAT_REUSE_MS` after a
 *      successful response, callers reuse the cached payload instead of
 *      re-fetching. The BE Redis cache (BuildingChatCache, 60 s TTL) is
 *      authoritative for cross-tab freshness; this purely-client window
 *      just collapses the bursts that arrive within the same render cycle.
 *
 * `invalidateBuildingChat(buildingId)` drops the cached payload so the
 * next call hits the wire — use it after mutations (mark-as-read, mute,
 * leave, etc.) where the local cache is known to be stale.
 */

import { callAPI } from "./apiUtils";

const BUILDING_CHAT_REUSE_MS = 500;

const inFlight = new Map();
const lastResponse = new Map();

function keyFor({ buildingId, userId, viewByOwner = false }) {
  // viewByOwner produces a different SP path on the server, so it must
  // participate in the cache key.
  return `${buildingId}::${userId || ""}::${viewByOwner ? 1 : 0}`;
}

/**
 * Fetch the chat-group list for a building.
 *
 * Returns the same shape `callAPI('/building/:id/chat', ...)` would —
 * i.e. an axios-style `{ status, data }`. Errors are not rethrown
 * (callAPI itself catches them), so callers can rely on a resolved
 * promise.
 */
export function getBuildingChat({ buildingId, userId, lang, viewByOwner = false, force = false }) {
  if (!buildingId) {
    return Promise.resolve({
      status: 400,
      data: { status: false, message: "missing buildingId" },
    });
  }

  const key = keyFor({ buildingId, userId, viewByOwner });
  const now = Date.now();

  if (!force) {
    const existing = inFlight.get(key);
    if (existing) return existing;

    const cached = lastResponse.get(key);
    if (cached && now - cached.at < BUILDING_CHAT_REUSE_MS) {
      return Promise.resolve(cached.response);
    }
  } else {
    lastResponse.delete(key);
  }

  const params = { userId, lang };
  if (viewByOwner) params.viewByOwner = true;

  const promise = (async () => {
    try {
      const response = await callAPI(`/building/${buildingId}/chat`, params, "GET");
      // Only cache successful payloads (avoids reusing transient 500s).
      if (response && response.data && response.data.status) {
        lastResponse.set(key, { response, at: Date.now() });
      }
      return response;
    } finally {
      if (inFlight.get(key) === promise) {
        inFlight.delete(key);
      }
    }
  })();

  inFlight.set(key, promise);
  return promise;
}

/**
 * Drop cached payloads for a building. Call after a mutation (mark-as-
 * read, mute toggle, member leave, group create) that makes the cached
 * response stale. Without an argument, every cached entry is cleared.
 */
export function invalidateBuildingChat(buildingId) {
  if (!buildingId) {
    lastResponse.clear();
    return;
  }
  const prefix = `${buildingId}::`;
  for (const k of Array.from(lastResponse.keys())) {
    if (k.startsWith(prefix)) lastResponse.delete(k);
  }
}
